import Result from "@Core/Result";
import { ServiceBase } from "@Core/ServiceBase";
import {
    IAssetDetails,
    IAudienceDetails, ILicenseCreationDetails, ILicenseDetails
} from "@Models/ILicenseDetails";
import {IEntity} from "@Models/IEntity";
import { IEmptyObject } from "@Components/shared/types";

export default class LicenseService extends ServiceBase {
    /*
     * Fetch all license records.
     */
    public async get(): Promise<Result<ILicenseDetails[]>> {
        return await this.requestJson<ILicenseDetails[]>({
            url: `/api/License/Get`,
            method: "GET",
        });
    }

    /*
     * Fetch license records assigned to the group ids provided.
     */
    public async getGroup(groupIds: string[]): Promise<Result<ILicenseDetails[]>> {
        return await this.requestJson<ILicenseDetails[]>({
            url: `/api/License/GetGroup/${groupIds.join(",")}`,
            method: "GET",
        });
    }

    /*
     * Fetch license records assigned to the user ids provided.
     */
    public async getUser(userIds: string[]): Promise<Result<ILicenseDetails[]>> {
        return await this.requestJson<ILicenseDetails[]>({
            url: `/api/License/GetUser/${userIds.join(",")}`,
            method: "GET",
        });
    }

    /*
     * Create license record
     */
    public async create(model: ILicenseCreationDetails): Promise<Result<IEntity>> {
        return await this.requestJson<IEntity>({
            url: `/api/License/Create`,
            method: "POST",
            data: model,
        });
    }

    /*
     * Update license record
     */
    public async update(model: ILicenseDetails): Promise<Result<IEntity>> {
        return await this.requestJson<IEntity>({
            url: `/api/License/Update`,
            method: "PATCH",
            data: model,
        });
    }

    /*
     * Delete license
     */
    public async delete(licenseId: string): Promise<Result<IEmptyObject>> {
        return await this.requestJson({
            url: `/api/License/Delete?license=${licenseId}`,
            method: "DELETE",
        });
    }

    /*
     * Fetch all audience records.
     */
    public async getAudiences(): Promise<Result<IAudienceDetails[]>> {
        return await this.requestJson<IAudienceDetails[]>({
            url: `/api/License/GetAudiences`,
            method: "GET",
        });
    }

    /*
     * Create a new audience.
     */
    public async createAudience(model: IAudienceDetails): Promise<Result<IEntity>> {
        return await this.requestJson<IEntity>({
            url: `/api/License/CreateAudience`,
            method: "POST",
            data: model,
        });
    }

    /*
     * Update an audience.
     */
    public async updateAudience(model: IAudienceDetails): Promise<Result<IEmptyObject>> {
        return await this.requestJson({
            url: `/api/License/UpdateAudience`,
            method: "PATCH",
            data: model,
        });
    }

    /*
     * Delete audiences.
     */
    public async deleteAudience(audienceId: string): Promise<Result<IEmptyObject>> {
        return await this.requestJson({
            url: `/api/License/DeleteAudience?audience-id=${audienceId}`,
            method: "DELETE",
        });
    }

    /*
     * Create a new audience instance.
     */
    public async createAudienceInstance(audienceId: string, licenseId: string): Promise<Result<IEntity>> {
        return await this.requestJson<IEntity>({
            url: `/api/License/CreateAudienceInstance?audience-id=${audienceId}&license-id=${licenseId}`,
            method: "POST",
        });
    }

    /*
     * Delete an audience instance from a license.
     */
    public async deleteAudienceInstance(audienceInstanceId: string): Promise<Result<IEmptyObject>> {
        return await this.requestJson({
            url: `/api/License/DeleteAudienceInstance?audience-instance-id=${audienceInstanceId}`,
            method: "DELETE",
        });
    }

    /*
     * Fetch all unassigned asset records for the specified product.
     */
    public async getUnassignedAssets(productId: string): Promise<Result<IAssetDetails[]>> {
        return await this.requestJson<IAssetDetails[]>({
            url: `/api/License/GetUnassignedAssets?product-id=${productId}`,
            method: "GET",
        });
    }
}